<template>
  <Card cardClasses="shadow-sm border-0">
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h4 class="m-0 text-primary">
        <i class="bi bi-arrow-left-right me-2"></i>Store Transfers Management
      </h4>
      <div class="d-flex">
        <button
            v-if="profile.role !== 'user'"
            class="btn btn-primary"
            @click="transferModal = true; transferMode = 'add'">
          <i class="bi bi-plus-circle me-1"></i> Add Store Transfer
        </button>
      </div>
    </div>

    <div class="container-fluid p-0">
      <div v-if="loading" class="text-center py-5">
        <Spinner spinner-class="mb-1" />
        <p class="text-muted mt-2">Loading store transfers...</p>
      </div>

      <div v-if="!loading" class="transfers-content fade-in">
        <div class="d-flex flex-wrap align-items-center justify-content-between mb-3">
          <div class="d-flex align-items-center mb-2 mb-sm-0">
            <span class="badge bg-primary rounded-pill me-2">{{ transfers.total }}</span>
            <span class="text-muted">Store Transfers</span>
          </div>
          <div class="d-flex gap-2 align-items-center">
            <UInput v-model="dataTableSearch" size="md" placeholder="Search transfer" class="mb-0 w-auto" />
            <button class="btn btn-primary" @click="onSearchClick">
              <i class="bi bi-search me-1"></i> Search
            </button>
            <button class="btn btn-outline-secondary" @click="onClearSearchClick" :disabled="!dataTableSearch || !dataTableSearch.length">
              <i class="bi bi-x-circle me-1"></i> Clear
            </button>
          </div>
        </div>

        <div class="card border-0 shadow-sm mb-4">
          <div class="table-responsive">
            <table class="table table-hover align-middle mb-0">
              <thead>
                <tr>
                  <th>#</th>
                  <th>CODE</th>
                  <th>FROM STORE</th>
                  <th>TO STORE</th>
                  <th>BY USER</th>
                  <th>DESCRIPTION</th>
                  <th>STATUS</th>
                  <th>DATE</th>
                  <th class="text-end">ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(transfer, index) in transfers.data" :key="transfer.id" class="transfer-row" :class="{'bg-light': index % 2 === 0}">
                  <td>{{ transfer.id.split("-").pop() }}</td>
                  <td>{{ transfer.code }}</td>
                  <td>{{ transfer.from_store?.name || '-' }}</td>
                  <td>{{ transfer.to_store?.name || '-' }}</td>
                  <td>{{ transfer.user?.first_name }} {{ transfer.user?.last_name }}</td>
                  <td>{{ transfer.description || '-' }}</td>
                  <td>
                    <Badge pill :type="getPillByStatus(transfer.status)">
                      {{ transfer.status }}
                    </Badge>
                  </td>
                  <td>{{ formatDate(transfer.created_at) }}</td>
                  <td class="text-end">
                    <button class="btn btn-sm btn-outline-primary" @click="transferDetails(transfer.id)">
                      <i class="bi bi-eye"></i> Details
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div class="d-flex flex-wrap pt-3 pb-4 align-items-end mx-4 justify-content-between">
          <div class="me-sm-4 mb-sm-0 mb-3">
            <p class="mb-2 small">Rows per page</p>
            <select class="form-select" v-model.number="dataTablePerPage" data="">
              <option v-for="item in [5,10,25,50,100]" :key="item" :value="item">{{item}}</option>
            </select>
          </div>
          <div>
            <Pagination
              v-model="dataTablePage"
              :pages="totalPages"
              :threshold="2">
              <template #previous>Prev</template>
              <template #next>Next</template>
            </Pagination>
          </div>
        </div>

        <div class="d-flex justify-content-center">
          <button class="btn btn-outline-secondary" @click="$router.go(-1)">
            <i class="bi bi-arrow-left me-1"></i> Back
          </button>
        </div>
      </div>
    </div>

    <Modal :open="transferModal" @closed="transferModal = false" dialog-size="modal-lg">
      <div class="modal-header bg-light">
        <h5 class="modal-title m-0" v-if="transferMode === 'add'">
          <i class="bi bi-plus-circle me-2"></i>Add Store Transfer
        </h5>
        <h5 class="modal-title m-0" v-else>
          <i class="bi bi-pencil-square me-2"></i>Edit Store Transfer
        </h5>
        <button @click="transferModal = false" type="button" class="btn-close outline-none" data-dismiss="modal" aria-label="Close">
        </button>
      </div>
      <div class="modal-body">
        <form @submit.prevent="">
          <div class="row">
            <div class="col-md-6">
              <div class="form-group mb-3">
                <label for="code" class="form-label fw-bold">Transfer Code:</label>
                <div class="input-group">
                  <span class="input-group-text"><i class="bi bi-upc"></i></span>
                  <input type="text" class="form-control" placeholder="Code" v-model="transfer.code" readonly>
                </div>
                <small class="form-text text-muted">Auto-generated code</small>
              </div>
            </div>
            <div class="col-md-6">
              <div class="form-group mb-3">
                <label for="from_store" class="form-label fw-bold">From Store:</label>
                <div class="input-group">
                  <span class="input-group-text"><i class="bi bi-shop"></i></span>
                  <input type="text" class="form-control" :value="store.name" disabled>
                </div>
                <small class="form-text text-muted">Current store</small>
              </div>
            </div>
          </div>

          <div class="row">
            <div class="col-md-12">
              <div class="form-group mb-3">
                <label for="to_store" class="form-label fw-bold">To Store:</label>
                <div class="input-group">
                  <span class="input-group-text"><i class="bi bi-shop"></i></span>
                  <select class="form-select" v-model="transfer.to_store_id" data="">
                    <option value="" disabled selected>Select destination store</option>
                    <option v-for="s in stores" :key="s.id" :value="s.id" :disabled="s.id === store.id">{{ s.name }}</option>
                  </select>
                </div>
                <small class="form-text text-muted">Select the store to transfer items to</small>
              </div>
            </div>
          </div>

          <div class="row mt-3">
            <div class="col-12">
              <div class="form-group mb-3">
                <label for="description" class="form-label fw-bold">Description:</label>
                <div class="input-group">
                  <span class="input-group-text"><i class="bi bi-card-text"></i></span>
                  <textarea class="form-control" placeholder="Enter transfer description" v-model="transfer.description" rows="3"></textarea>
                </div>
                <small class="form-text text-muted">Provide details about this transfer</small>
              </div>
            </div>
          </div>

          <div class="row mt-4">
            <div class="col-12">
              <div class="alert alert-info d-flex align-items-center">
                <i class="bi bi-info-circle-fill me-2"></i>
                <div>
                  <strong>Note:</strong> After creating the transfer, you'll be able to add products to it.
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
      <div class="modal-footer bg-light">
        <button type="button" class="btn btn-outline-secondary" data-dismiss="modal" @click="transferModal = false">
          <i class="bi bi-x-circle me-1"></i> Cancel
        </button>
        <button type="button" class="btn btn-primary" v-if="transferMode === 'add'" @click="createTransfer">
          <i class="bi bi-plus-circle me-1"></i> Create Transfer
        </button>
        <button type="button" class="btn btn-primary" v-else @click="updateTransfer(transfer.id)">
          <i class="bi bi-check-circle me-1"></i> Update Transfer
        </button>
      </div>
    </Modal>

  </Card>
</template>

<script>
import storeTransferService from "@/api/storeTransferService";
import storeService from "@/api/storeService";
import router from "@/router";
import {decrypt} from "@/utils/crypto";
import formatDate from "@/utils/formatDate";

export default {
  name: "StoreTransfers",

  data(){
    const profile = JSON.parse(decrypt(sessionStorage.getItem('profile')))
    const store = JSON.parse(decrypt(sessionStorage.getItem('store')))
    const organization = JSON.parse(decrypt(sessionStorage.getItem('organization')))
    return {
      organization: organization,
      store: store,
      stores: [],
      allStores: [],
      profile: profile,
      loading: true,
      transfer: {
        organization_id: organization.id,
        from_store_id: store.id,
        to_store_id: '',
        user_id: profile.id,
        code: '',
        description: '',
      },
      transferMode: 'add',
      transferModal: false,
      transfers: {
        data: []
      },
      dataTableSearch: this.$route?.query?.search ?? '',
      dataTablePage: this.$route?.query?.page ? parseInt(this.$route.query.page) : 1,
      dataTablePerPage: this.$route?.query?.per_page ? parseInt(this.$route.query.per_page) : 10,
      subscription: {
        package: {}
      },
    }
  },

  methods:{
    formatDate,
    getPillByStatus(cellData) {
      switch (cellData) {
        case 'pending':
          return 'warning';
        case 'complete':
          return 'success';
      }
    },
    async getStoreTransfers() {
      this.loading = true
      const params = {
        page: this.dataTablePage,
        per_page: this.dataTablePerPage,
        search: this.dataTableSearch?.toString().trim() || undefined,
      }
      this.transfers = await storeTransferService.getStoreTransfers(params)
      this.loading = false
    },
    onSearchClick(){
      this.dataTablePage = 1
      this.updateRouteQuery()
      this.getStoreTransfers()
    },
    onClearSearchClick(){
      if (!this.dataTableSearch) return;
      this.dataTableSearch = ''
      this.dataTablePage = 1
      this.updateRouteQuery()
      this.getStoreTransfers()
    },
    updateRouteQuery() {
      const query = {
        ...this.$route.query,
        page: this.dataTablePage !== 1 ? this.dataTablePage : undefined,
        per_page: this.dataTablePerPage !== 10 ? this.dataTablePerPage : undefined,
        search: this.dataTableSearch && this.dataTableSearch.trim().length ? this.dataTableSearch.trim() : undefined,
      }
      Object.keys(query).forEach(k => query[k] === undefined && delete query[k])
      this.$router.replace({ path: this.$route.path, query })
    },
    transferDetails(id) {
      router.push('/store-transfer/' + id)
    },
    async createTransfer() {
      let response = await storeTransferService.createStoreTransfer(this.transfer)
      if (response) {
        this.transferModal = false
        router.push('/store-transfer/' + response.id)
      }
    },
  },

  watch: {
    dataTablePerPage(){
      this.dataTablePage = 1;
      this.updateRouteQuery()
      this.getStoreTransfers()
    },
    dataTablePage(){
      this.updateRouteQuery()
      this.getStoreTransfers()
    },
    async transferModal(val) {
      if (val) {
        let nextNo = await storeTransferService.getStoreTransferNextNo()
        this.transfer.code = nextNo.next
      }
    }
  },

  created: async function () {
    await this.getStoreTransfers()
    this.allStores = await storeService.getStores();
    this.subscription = JSON.parse(decrypt(sessionStorage.getItem('subscription')))
  },

  computed: {
    totalPages(){
      return this.transfers?.last_page || 1
    },
    stores() {
      return this.allStores.filter(s => s.id !== this.store.id)
    }
  },

}
</script>

<style scoped>
.transfers-content {
  padding: 0.5rem;
}

.table th {
  font-weight: 600;
  text-transform: uppercase;
  font-size: 0.7rem;
  letter-spacing: 0.5px;
  color: #6b7280;
  border-top: none;
  padding: 1rem 1.5rem;
  background-color: #f9fafb;
  border-bottom: 1px solid #e9ecef;
}

.table td {
  padding: 1rem 1.5rem;
  vertical-align: middle;
  border-color: #e9ecef;
  color: #4b5563;
}

.transfer-row {
  transition: all 0.2s ease;
}

.transfer-row:hover {
  background-color: #f8f9fa;
  transform: translateY(-2px);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
}

.badge {
  font-weight: 500;
  padding: 0.35em 0.65em;
  font-size: 0.75em;
  border-radius: 6px;
}

.fade-in {
  animation: fadeIn 0.3s ease-in-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
